import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api, setToken, getToken } from "@/lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const appRef = useRef(null);
  const saveTimer = useRef(null);

  appRef.current = application;

  // Bootstrap from token
  useEffect(() => {
    (async () => {
      const t = getToken();
      if (!t) {
        setLoading(false);
        return;
      }
      try {
        const { user: u } = await api.me();
        setUser(u);
        if (u.role === "applicant") {
          const appn = await api.getMyApplication();
          setApplication(appn);
        }
      } catch (e) {
        setToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadApplication = useCallback(async () => {
    const appn = await api.getMyApplication();
    setApplication(appn);
    return appn;
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, user: u } = await api.login({ email, password });
    setToken(token);
    setUser(u);
    if (u.role === "applicant") {
      sessionStorage.removeItem("assk_marking_seen");
      const appn = await api.getMyApplication();
      setApplication(appn);
    }
    return u;
  }, []);

  const registerRequestOtp = useCallback(async (email) => {
    return api.registerRequestOtp({ email });
  }, []);

  const registerVerify = useCallback(async (email, code) => {
    return api.registerVerify({ email, code });
  }, []);

  const registerComplete = useCallback(async (email, ticket, password) => {
    const { token, user: u } = await api.registerComplete({ email, ticket, password });
    setToken(token);
    setUser(u);
    sessionStorage.removeItem("assk_marking_seen");
    const appn = await api.getMyApplication();
    setApplication(appn);
    return u;
  }, []);

  const officerLogin = useCallback(async (username, password) => {
    const { token, user: u } = await api.officerLogin({ username, password });
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.changePassword({ currentPassword, newPassword });
    setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setApplication(null);
    sessionStorage.removeItem("assk_marking_seen");
  }, []);

  // ---- idle auto-logout for officer/admin (15 min inactivity) ----
  useEffect(() => {
    const role = user?.role;
    const isPrivileged = role && role !== "applicant";
    if (!isPrivileged) return;
    const IDLE_MS = 15 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
        try { toast.info("निष्क्रियतेमुळे आपण लॉगआउट झालात. कृपया पुन्हा लॉगिन करा."); } catch (e) {}
        window.location.href = "/officer";
      }, IDLE_MS);
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [user?.role, logout]);

  // ---- persistence ----
  const persistNow = useCallback(async () => {
    const a = appRef.current;
    if (!a || a.status !== "DRAFT") return;
    try {
      const updated = await api.updateApplication(a.id, { data: a.data, currentStep: a.currentStep });
      // keep local data (avoid clobbering fast typing); only trust server timestamps
      setApplication((prev) => (prev ? { ...prev, updatedAt: updated.updatedAt } : prev));
    } catch (e) {
      /* silent autosave failure */
    }
  }, []);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persistNow, 1200);
  }, [persistNow]);

  const setField = useCallback((name, value) => {
    setApplication((prev) => (prev ? { ...prev, data: { ...prev.data, [name]: value } } : prev));
    scheduleSave();
  }, [scheduleSave]);

  const setFields = useCallback((obj) => {
    setApplication((prev) => (prev ? { ...prev, data: { ...prev.data, ...obj } } : prev));
    scheduleSave();
  }, [scheduleSave]);

  const removeFields = useCallback((predicate) => {
    setApplication((prev) => {
      if (!prev) return prev;
      const next = { ...prev.data };
      Object.keys(next).forEach((k) => predicate(k) && delete next[k]);
      return { ...prev, data: next };
    });
    scheduleSave();
  }, [scheduleSave]);

  const goToStep = useCallback((step) => {
    setApplication((prev) => (prev ? { ...prev, currentStep: step } : prev));
    setTimeout(persistNow, 0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [persistNow]);

  const saveDraft = useCallback(async () => {
    await persistNow();
  }, [persistNow]);

  const uploadFile = useCallback(async (field, file) => {
    const a = appRef.current;
    if (!a) throw new Error("No application");
    const res = await api.uploadFile(a.id, field, file);
    setField(field, { name: res.name, size: res.size, fileId: res.fileId });
    return res;
  }, [setField]);

  const requestSubmitOtp = useCallback(async () => {
    const a = appRef.current;
    if (!a) throw new Error("No application");
    await persistNow();
    return api.requestSubmitOtp(a.id);
  }, [persistNow]);

  const submitApplication = useCallback(async (code) => {
    const a = appRef.current;
    if (!a) return;
    await persistNow();
    const updated = await api.submitApplication(a.id, code);
    setApplication(updated);
    return updated;
  }, [persistNow]);

  const resetApplication = useCallback(async () => {
    const fresh = await api.newApplication();
    setApplication(fresh);
    return fresh;
  }, []);

  const value = {
    user, loading, application, setUser,
    login, registerRequestOtp, registerVerify, registerComplete, officerLogin, changePassword, logout,
    loadApplication, setField, setFields, removeFields,
    goToStep, saveDraft, uploadFile, submitApplication, requestSubmitOtp, resetApplication,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
